/**
 * Dynamic Noto Font Configuration
 *
 * Maps language codes to their appropriate DM Sans font variants.
 * DM is designed to cover all Unicode scripts, but variants are
 * loaded dynamically to avoid downloading unnecessary font files.
 */

// Google Fonts API base URL
export const GOOGLE_FONTS_API = "https://fonts.googleapis.com/css2";

// Default font weights to load
export const DEFAULT_WEIGHTS = "400;500;600;700";

/**
 * Language to DM font variant mapping
 *
 * Structure:
 * - family: Google Fonts family name
 * - weights: Font weights to load (optional, defaults to DEFAULT_WEIGHTS)
 * - fallback: Additional fallback fonts
 * - direction: 'ltr' or 'rtl' for text direction
 */
export const fontConfig = {
  // Latin script languages (Western European)
  en: {
    family: "DM Sans",
    fallback: "system-ui, -apple-system, sans-serif",
    direction: "ltr",
  },
  de: {
    family: "DM Sans",
    fallback: "system-ui, -apple-system, sans-serif",
    direction: "ltr",
  },
  fr: {
    family: "DM Sans",
    fallback: "system-ui, -apple-system, sans-serif",
    direction: "ltr",
  },
  es: {
    family: "DM Sans",
    fallback: "system-ui, -apple-system, sans-serif",
    direction: "ltr",
  },
  pt: {
    family: "DM Sans",
    fallback: "system-ui, -apple-system, sans-serif",
    direction: "ltr",
  },
  it: {
    family: "DM Sans",
    fallback: "system-ui, -apple-system, sans-serif",
    direction: "ltr",
  },
  nl: {
    family: "DM Sans",
    fallback: "system-ui, -apple-system, sans-serif",
    direction: "ltr",
  },
  pl: {
    family: "DM Sans",
    fallback: "system-ui, -apple-system, sans-serif",
    direction: "ltr",
  },

  // Cyrillic script languages
  ru: {
    family: "DM Sans",
    fallback: "system-ui, sans-serif",
    direction: "ltr",
  },
  uk: {
    family: "DM Sans",
    fallback: "system-ui, sans-serif",
    direction: "ltr",
  },

  // Greek
  el: {
    family: "DM Sans",
    fallback: "system-ui, sans-serif",
    direction: "ltr",
  },

  // CJK (Chinese, Japanese, Korean)
  ja: {
    family: "DM Sans JP",
    weights: "400;500;700",
    fallback: "Hiragino Sans, Yu Gothic, sans-serif",
    direction: "ltr",
  },
  ko: {
    family: "DM Sans KR",
    weights: "400;500;700",
    fallback: "Malgun Gothic, Apple SD Gothic Neo, sans-serif",
    direction: "ltr",
  },
  zh: {
    family: "DM Sans SC", // Simplified Chinese
    weights: "400;500;700",
    fallback: "PingFang SC, Microsoft YaHei, sans-serif",
    direction: "ltr",
  },
  "zh-CN": {
    family: "DM Sans SC",
    weights: "400;500;700",
    fallback: "PingFang SC, Microsoft YaHei, sans-serif",
    direction: "ltr",
  },
  "zh-TW": {
    family: "DM Sans TC", // Traditional Chinese
    weights: "400;500;700",
    fallback: "PingFang TC, Microsoft JhengHei, sans-serif",
    direction: "ltr",
  },
  "zh-HK": {
    family: "DM Sans HK", // Hong Kong
    weights: "400;500;700",
    fallback: "PingFang HK, Microsoft JhengHei, sans-serif",
    direction: "ltr",
  },

  // RTL (Right-to-Left) languages
  ar: {
    family: "DM Sans Arabic",
    weights: "400;500;600;700",
    fallback: "Tahoma, Arial, sans-serif",
    direction: "rtl",
  },
  he: {
    family: "DM Sans Hebrew",
    weights: "400;500;600;700",
    fallback: "Arial Hebrew, sans-serif",
    direction: "rtl",
  },
  fa: {
    family: "DM Sans Arabic", // Persian uses Arabic script
    weights: "400;500;600;700",
    fallback: "Tahoma, Arial, sans-serif",
    direction: "rtl",
  },
  ur: {
    family: "DM Sans Arabic", // Urdu uses Arabic script
    weights: "400;500;600;700",
    fallback: "Tahoma, Arial, sans-serif",
    direction: "rtl",
  },

  // South Asian languages
  hi: {
    family: "DM Sans Devanagari",
    weights: "400;500;600;700",
    fallback: "Mangal, sans-serif",
    direction: "ltr",
  },
  bn: {
    family: "DM Sans Bengali",
    weights: "400;500;700",
    fallback: "Vrinda, sans-serif",
    direction: "ltr",
  },
  ta: {
    family: "DM Sans Tamil",
    weights: "400;500;700",
    fallback: "Latha, sans-serif",
    direction: "ltr",
  },
  te: {
    family: "DM Sans Telugu",
    weights: "400;500;700",
    fallback: "Gautami, sans-serif",
    direction: "ltr",
  },
  gu: {
    family: "DM Sans Gujarati",
    weights: "400;500;700",
    fallback: "Shruti, sans-serif",
    direction: "ltr",
  },
  pa: {
    family: "DM Sans Gurmukhi",
    weights: "400;500;700",
    fallback: "Raavi, sans-serif",
    direction: "ltr",
  },

  // Southeast Asian languages
  th: {
    family: "DM Sans Thai",
    weights: "400;500;600;700",
    fallback: "Tahoma, sans-serif",
    direction: "ltr",
  },
  vi: {
    family: "DM Sans",
    fallback: "Arial, sans-serif",
    direction: "ltr",
  },
  my: {
    family: "DM Sans Myanmar",
    weights: "400;500;700",
    fallback: "Myanmar Text, sans-serif",
    direction: "ltr",
  },
  km: {
    family: "DM Sans Khmer",
    weights: "400;500;700",
    fallback: "Khmer UI, sans-serif",
    direction: "ltr",
  },

  // Other scripts
  ka: {
    family: "DM Sans Georgian",
    weights: "400;500;600;700",
    fallback: "sans-serif",
    direction: "ltr",
  },
  hy: {
    family: "DM Sans Armenian",
    weights: "400;500;600;700",
    fallback: "sans-serif",
    direction: "ltr",
  },
  am: {
    family: "DM Sans Ethiopic",
    weights: "400;500;700",
    fallback: "sans-serif",
    direction: "ltr",
  },
};

/**
 * Get font configuration for a language
 * Falls back to English if language not found
 */
export function getFontConfig(lang) {
  // Try exact match first
  if (fontConfig[lang]) {
    return fontConfig[lang];
  }

  // Try base language (e.g., 'zh' for 'zh-Hans')
  const baseLang = lang.split("-")[0];
  if (fontConfig[baseLang]) {
    return fontConfig[baseLang];
  }

  // Default to English
  return fontConfig.en;
}

/**
 * Build Google Fonts URL for a language
 */
export function buildGoogleFontsUrl(lang) {
  const config = getFontConfig(lang);
  const weights = config.weights || DEFAULT_WEIGHTS;
  const family = config.family.replace(/ /g, "+");

  return `${GOOGLE_FONTS_API}?family=${family}:wght@${weights}&display=swap`;
}

/**
 * Get CSS font-family string for a language
 */
export function getFontFamily(lang) {
  const config = getFontConfig(lang);
  return `"${config.family}", ${config.fallback}`;
}

/**
 * Get text direction for a language
 */
export function getTextDirection(lang) {
  const config = getFontConfig(lang);
  return config.direction || "ltr";
}

/**
 * Check if a language uses RTL script
 */
export function isRTL(lang) {
  return getTextDirection(lang) === "rtl";
}

export default fontConfig;
